import { api } from "./api";
import { config } from "../config";
import { getUserAuth } from "../helpers";

const prefix = "/notification";

/* ------------------------------------------------------------------ */
/* Envelope + domain types                                            */
/* ------------------------------------------------------------------ */

export interface IApiEnvelope<T> {
  message: string;
  totalRecords?: number;
  currentRecords?: number;
  data: T;
  meta?: Record<string, any>;
}

export type NotificationPriority = "LOW" | "NORMAL" | "HIGH";
export type NotificationStatus = "ACTIVE" | "ARCHIVED";

export interface INotification {
  notification_uuid: string;
  event_key: string;
  module: string;
  title: string;
  body: string;
  action_url: string | null;
  payload: Record<string, any> | null;
  priority: NotificationPriority;
  reference_uuid_column_name: string | null;
  reference_uuid_column_value: string | null;
  actor_user_uuid: string | null;
  actor_user_name: string | null;
  channels_sent: Record<string, string>;
  is_read: 0 | 1;
  read_ts: string | null;
  status: NotificationStatus;
  insert_ts: string;
  // Extra columns the backend also returns — not needed to render a row.
  notification_unique_id?: number;
  company_outlet_uuid?: string;
  user_uuid?: string;
  create_ts?: string;
  created_by_uuid?: string | null;
  created_by_name?: string | null;
  modified_by_uuid?: string | null;
  modified_by_name?: string | null;
}

export interface INotificationListParams {
  pageNo?: number;
  itemPerPage?: number;
  is_read?: boolean;
  module?: string | null;
  event_key?: string | null;
  reference_uuid_column_value?: string | null;
  from_date?: string | null;
  to_date?: string | null;
  status?: NotificationStatus;
}

export interface IUserDevice {
  user_device_uuid: string;
  provider: string;
  platform: string;
  device_name: string | null;
  user_agent: string | null;
  app_version: string | null;
  last_seen_ts: string;
  company_outlet_uuid: string;
}

export interface IUserNotificationPreference {
  user_notification_preference_uuid: string;
  event_key: string | null;
  is_muted: 0 | 1;
  channels: string[] | null;
  status: string;
}

export interface IMasterNotificationEvent {
  master_notification_event_uuid: string;
  event_key: string;
  module: string;
  event_name: string;
  description: string;
  default_channels: string[];
  priority: NotificationPriority;
  status: string;
}

export interface INotificationConfig extends Partial<IMasterNotificationEvent> {
  company_outlet_uuid: string;
  event_key: string;
  channels: string[];
  recipient_roles?: string[];
  recipient_users?: { user_uuid: string }[];
  notify_assigned_user?: boolean;
  notify_record_owner?: boolean;
  exclude_actor?: boolean;
}

export type BroadcastTargetType = "USER" | "ROLE" | "OUTLET" | "ALL";

export interface IBroadcastPayload {
  target_type: BroadcastTargetType;
  title: string;
  body: string;
  priority?: NotificationPriority;
  action_url?: string;
  channels?: string[];
  user_uuids?: string[];
  role_uuids?: string[];
  role_value?: string;
  company_outlet_uuid?: string;
}

/* ------------------------------------------------------------------ */
/* Stream URL (EventSource can't set headers, so the token goes in    */
/* the query string)                                                  */
/* ------------------------------------------------------------------ */

export const getNotificationStreamUrl = () => {
  const token = getUserAuth();
  return `${config.api.baseUrl}${prefix}/subscribe-stream?token=${encodeURIComponent(
    token || "",
  )}`;
};

/* ------------------------------------------------------------------ */
/* Inbox                                                              */
/* ------------------------------------------------------------------ */

export const notificationAPI = {
  getNotifications: (params: INotificationListParams) =>
    api
      .get<IApiEnvelope<INotification[]>>(`${prefix}/get-notification`, { params })
      .then((res) => res.data),

  getUnreadCount: () =>
    api
      .get<IApiEnvelope<{ unread_count: number }>>(`${prefix}/get-unread-count`)
      .then((res) => res.data),

  markNotificationRead: (notification_uuids: string[]) =>
    api
      .post<IApiEnvelope<{ updated: number }>>(`${prefix}/mark-notification-read`, {
        notification_uuids,
      })
      .then((res) => res.data),

  markAllNotificationRead: () =>
    api
      .post<IApiEnvelope<{ ok: boolean; updated: number }>>(
        `${prefix}/mark-all-notification-read`,
        {},
      )
      .then((res) => res.data),

  // Archives every already-read row (status → ARCHIVED). Unread rows are
  // left alone, so the badge count does not change.
  clearAllRead: () =>
    api
      .post<IApiEnvelope<{ ok: boolean; cleared: number }>>(`${prefix}/clear-all-read`, {})
      .then((res) => res.data),

  getStreamStatus: () =>
    api
      .get<IApiEnvelope<{ pid: number; active_clients: number; active_users: number }>>(
        `${prefix}/get-stream-status`,
      )
      .then((res) => res.data),

  /* ---------------------------- Push ---------------------------- */

  getVapidPublicKey: () =>
    api
      .get<IApiEnvelope<{ public_key: string; configured: boolean }>>(
        `${prefix}/get-vapid-public-key`,
      )
      .then((res) => res.data),

  registerDevice: (body: {
    endpoint: string;
    p256dh_key: string;
    auth_key: string;
    platform: string;
    user_agent: string;
  }) =>
    api
      .post<IApiEnvelope<{ user_device_uuid: string; provider: string }>>(
        `${prefix}/register-device`,
        body,
      )
      .then((res) => res.data),

  unregisterDevice: (body: { endpoint: string }) =>
    api
      .post<IApiEnvelope<{ ok: boolean; skipped?: string }>>(
        `${prefix}/unregister-device`,
        body,
      )
      .then((res) => res.data),

  getDevices: () =>
    api.get<IApiEnvelope<IUserDevice[]>>(`${prefix}/get-device`).then((res) => res.data),

  sendTestPush: (body: { title: string; body: string }) =>
    api
      .post<IApiEnvelope<{ sent: number; failed: number }>>(`${prefix}/send-test-push`, body)
      .then((res) => res.data),

  /* ------------------------ Preferences --------------------------- */

  getMyPreference: (params?: { event_key?: string }) =>
    api
      .get<IApiEnvelope<IUserNotificationPreference[]>>(
        `${prefix}/get-my-notification-preference`,
        { params },
      )
      .then((res) => res.data),

  saveMyPreference: (body: {
    event_key?: string;
    is_muted?: boolean;
    channels?: string[];
  }) =>
    api
      .post<IApiEnvelope<any>>(`${prefix}/upsert-my-notification-preference`, body)
      .then((res) => res.data),

  /* ------------------------- Broadcast ----------------------------- */

  sendBroadcast: (body: IBroadcastPayload) =>
    api
      .post<
        IApiEnvelope<{ ok: boolean; target_type: string; total_sent: number; notification_uuids: string[] }>
      >(`${prefix}/send-broadcast`, body)
      .then((res) => res.data),

  /* ------------------------ Admin: events/config -------------------- */

  getNotificationEvents: (params?: { module?: string }) =>
    api
      .get<IApiEnvelope<IMasterNotificationEvent[]>>(`${prefix}/get-notification-event`, {
        params,
      })
      .then((res) => res.data),

  getNotificationConfig: (params: { company_outlet_uuid: string }) =>
    api
      .get<IApiEnvelope<INotificationConfig[]>>(`${prefix}/get-notification-config`, {
        params,
      })
      .then((res) => res.data),

  saveNotificationConfig: (body: INotificationConfig) =>
    api
      .post<IApiEnvelope<any>>(`${prefix}/upsert-notification-config`, body)
      .then((res) => res.data),
};
